import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { ReactiveDialogContentComponent } from '@app/shared/components/dialog/reactive-dialog/base/reactive-dialog-content/reactive-dialog-content.component';
import { FileDownloadService } from '@app/shared/services/file-download.service';
import { LowLevelApi } from '@app/shared/services/state/low-level-api.service';
import { DialogService } from '@app/shared/services/ui/dialog/dialog.service';
import { DisplayOption } from '@app/shared/services/ui/message/message';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Esign, EsignHauptStatus, GenehmigungFile, KeyValue, Person, Polizze } from '@o30/entities';
import FileSaver from 'file-saver';
import { first, take } from 'rxjs/operators';
import { DataService } from '../../../services/data/data.service';
import { MessageService } from '../../../services/ui/message/message.service';
import { SpacerComponent } from '../../spacer/spacer.component';
import { AbstractReactiveDialogComponent } from '../reactive-dialog/base/abstract-reactive-dialog-component';

@UntilDestroy()
@Component({
  selector:        'app-genehmigung-file-dialog',
  templateUrl:     './genehmigung-file-dialog.component.html',
  styleUrls:       ['./genehmigung-file-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:      true,
  imports:         [
    ReactiveDialogContentComponent,
    SpacerComponent,
    MatButton
  ]
})
export class GenehmigungFileDialogComponent extends AbstractReactiveDialogComponent<never, never, GenehmigungFileDialogConfig, never>
  implements OnInit {

  private dataService = inject(DataService);
  private messageService = inject(MessageService);
  private llapi = inject(LowLevelApi);
  private filedownloadService = inject(FileDownloadService);
  private adialogService = inject(DialogService);

  personId: Person['id'];
  polizzeId: Polizze['id'];
  genehmigungFile: GenehmigungFile;
  @ViewChild('fileChooser', {static: false}) fileChooser: ElementRef;

  @ViewChild('ngForm') ngForm: NgForm;
  @ViewChild('fileChooser', {static: false}) fileChooserRef: ElementRef;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.initConfig();
  }

  private initConfig() {
    const config = this.config;
    const fromConfig = [
      'personId',
      'polizzeId',
      'genehmigungFile'
    ];
    if (config) {
      for (const key of fromConfig) {
        if (config[key]) {
          this[key] = config[key];
        }
      }
    }
  }

  get config(): GenehmigungFileDialogConfig {
    return this.dialogInputData?.context || undefined;
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--events--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  onUnterschreibenClick() {
    this.adialogService.displayGenehmigungPrintDialog(this.polizzeId, this.genehmigungFile.key)
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this))
      .subscribe(_dialogResult => {
        if (_dialogResult?.data) {
          if (_dialogResult.data.status === EsignHauptStatus.ENVELOPE_EXISTS) {
            this.showURLDialog(_dialogResult.data);
          } else {
            this.llapi.patchValue('layout.spinner', true);
            this.dataService.createGenehmigungEsignAntragUnterschriftenProzess(this.polizzeId, this.genehmigungFile.key, _dialogResult.data)
              .pipe(
                first(),
                untilDestroyed(this))
              .subscribe(payload => {
                this.llapi.patchValue('layout.spinner', false);
                if (this.messageService.handlePayloadReturnSuccess(payload)) {
                  this.showURLDialog(_dialogResult.data);
                }
              });
          }
        }
      });
  }

  async onHerunterladenClick() {
    await this.filedownloadService.showOrDownloadFile(this.dataService.downloadGenehmigungFile(this.polizzeId, this.genehmigungFile.key));
  }

  public clickUpload(): void {
    this.fileChooserRef.nativeElement.click();
  }

  public onFilesAdded(): void {
    if (this.fileChooser.nativeElement.files && this.fileChooser.nativeElement.files.length > 0) {
      this.uploadAll(this.fileChooser.nativeElement.files[0]);
    }
  }

  private uploadAll(file: File): void {
    this.dataService.uploadFileGenehmigung(file)
      .pipe()
      .subscribe(val => {
        if (this.messageService.handlePayloadReturnSuccess(val, {displayOption: DisplayOption.SNACKBAR})) {
          this.dataService.uploadGenehmigungFile(this.polizzeId, this.genehmigungFile.key, {
              genehmigungFile: {
                key:   this.genehmigungFile.key,
                value: this.genehmigungFile.name
              },
              fileUpload:      {
                fileObjectIds: [val.data]
              }
            })
            .pipe(
              first(),
              untilDestroyed(this)
            )
            .subscribe(payload => {
              if (this.messageService.handlePayloadReturnSuccess(payload, {displayOption: DisplayOption.SNACKBAR})) {
                this.close();
              }
            });
        }
      });
  }

  private showURLDialog(esign: Esign): void {
    this.adialogService.displayGenehmigungEsignLinkDialog(esign, this.polizzeId, this.genehmigungFile.key)
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this))
      .subscribe((result) => {
        // Wenn der esign Dialog geschlossen wurde, dann den Genehmigungsdialog gleich mitschließen
        this.dialogRef.close();
      });
  }

  public openFile(file: KeyValue<Blob>): void {
    FileSaver.saveAs(file.value, file.key);
  }
}

export interface GenehmigungFileDialogConfig {
  type: 'mail' | 'print';
  personId: Person['id'];
  polizzeId: Polizze['id'];
  genehmigungFile: GenehmigungFile;
}
